import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Checkmark24Filled } from '@fluentui/react-icons';
import { Button } from '../ui';
import SpinnerIcon from '../icon/Spinner';
import { Alert, TYPE_MESSAGE } from '../alerts/Alert';
import type { SelectPlanProps } from './SelectPlan.types';

const DEFAULT_KEY_PREFIX = 'selectPlan';

export const SelectPlan: React.FC<SelectPlanProps> = ({
  companyId,
  onSelectPlan,
  isCreating = false,
  createError = null,
  plans,
  translationKeyPrefix = DEFAULT_KEY_PREFIX,
  noCompanyRedirectTo = '/companies',
  onNoCompany,
  blockLeaveUntilComplete = true,
  subscriptionCreatedSuccess = false,
  onSuccessRedirect,
  activePlanId = null,
  currentPlanButtonKey,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isAlertErrorVisible, setIsAlertErrorVisible] = useState(false);
  const [loadingPlanId, setLoadingPlanId] = useState<number | null>(null);
  const hasCompletedSelection = useRef(subscriptionCreatedSuccess);
  const p = (key: string) =>
    translationKeyPrefix ? `${translationKeyPrefix}.${key}` : key;

  useEffect(() => {
    if (subscriptionCreatedSuccess) {
      hasCompletedSelection.current = true;
      onSuccessRedirect?.();
    }
  }, [subscriptionCreatedSuccess, onSuccessRedirect]);

  useEffect(() => {
    if (createError) {
      setIsAlertErrorVisible(true);
      setLoadingPlanId(null);
    }
  }, [createError]);

  useEffect(() => {
    if (companyId == null) {
      if (onNoCompany) {
        onNoCompany();
      } else if (noCompanyRedirectTo) {
        navigate(noCompanyRedirectTo, { replace: true });
      }
    }
  }, [companyId, noCompanyRedirectTo, onNoCompany, navigate]);

  useEffect(() => {
    if (!blockLeaveUntilComplete || companyId == null || !isCreating) return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [blockLeaveUntilComplete, companyId, isCreating]);

  useEffect(() => {
    if (!blockLeaveUntilComplete || companyId == null || !isCreating) return;
    const handlePopState = () => {
      navigate(location.pathname, { replace: true, state: { companyId } });
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [blockLeaveUntilComplete, companyId, isCreating, location.pathname, navigate]);

  const handleSelectPlan = (planId: number) => {
    if (companyId == null) return;
    setLoadingPlanId(planId);
    onSelectPlan({
      companyId,
      planId,
      isTrial: true,
      autoRenew: false,
    });
  };

  if (companyId == null) {
    return null;
  }

  return (
    <div className="flex justify-center px-4 mt-9 text-neutral-20">
      <div className="w-full max-w-6xl">
        <div className="mb-12 text-center">
          <h1 className="mb-3 text-[32px] leading-[40px] font-bold text-neutral-20">
            {t(p('title'))}
          </h1>
          <p className="text-xl text-neutral-20">{t(p('subtitle'))}</p>
        </div>

        {createError && isAlertErrorVisible && (
          <div className="mb-6">
            <Alert
              typeMessage={TYPE_MESSAGE.ERROR}
              show
              handle={() => setIsAlertErrorVisible(false)}
              texte={
                createError?.details?.[0]?.translatedMessage ?? createError?.translatedMessage ?? t(p('errorCreatingSubscription'))
              }
            />
          </div>
        )}

        <div className="flex flex-col justify-center gap-6 md:flex-row">
          {plans.map((plan) => (
            <div
              key={plan.planId}
              className={`relative w-[432px] flex flex-col gap-6 p-7 transition-shadow border border-gray-100 rounded-2xl ${
                plan.recommended ? 'bg-blue-95' : 'bg-white'
              }`}
              style={{ boxShadow: '0px 3px 10px 0px #0000001A' }}
            >
              {plan.recommended && (
                <div className="absolute top-0 right-0 px-3 py-1 text-xs font-medium text-white rounded-bl-lg bg-neutral-12 rounded-tr-2xl">
                  {t(p('recommended'))}
                </div>
              )}

              <div className="flex flex-col gap-3 text-neutral-20">
                <h2 className="text-2xl text-left">{t(plan.titleKey)}</h2>
                <p className="text-sm text-left">{t(plan.descriptionKey)}</p>
                <div className="text-lg font-semibold">
                  {plan.priceRequestQuoteKey
                    ? t(plan.priceRequestQuoteKey)
                    : plan.priceKey
                      ? t(plan.priceKey)
                      : null}
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <p className="text-sm font-medium text-left uppercase text-neutral-20">
                  {t(p('featuresLabel'))}
                </p>
                {plan.includesLabelKey && (
                  <p className="text-xs text-left text-neutral-50">
                    {t(plan.includesLabelKey)}
                  </p>
                )}
                {(t(plan.featuresKey, { returnObjects: true }) as string[]).map(
                  (feature: string, index: number) => (
                    <div key={index} className="flex items-start gap-1">
                      <Checkmark24Filled className="w-4 h-4 flex-shrink-0 mt-0.5 text-primary" />
                      <span className="text-sm text-neutral-20">{feature}</span>
                    </div>
                  )
                )}
              </div>

              <Button
                type="button"
                className="w-full gap-2 mt-auto"
                variant={plan.recommended ? 'default' : 'secondary'}
                disabled={
                  isCreating ||
                  (activePlanId != null && plan.planId === activePlanId)
                }
                onClick={() => handleSelectPlan(plan.planId)}
              >
                {loadingPlanId === plan.planId && isCreating ? (
                  <SpinnerIcon />
                ) : activePlanId != null && plan.planId === activePlanId && currentPlanButtonKey ? (
                  t(currentPlanButtonKey)
                ) : (
                  t(plan.buttonKey)
                )}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SelectPlan;
